import { useState, useRef, useMemo } from 'react'
import { generateHomefrontV3 } from '../rules/homefrontAutogen'
import {
    HOMEFRONT_MAX_STRUCTURES,
    HOMEFRONT_MAX_PER_SHAPE,
    STRUCTURES_TO_WIN,
    YIELD_PER_LOT_PER_ROUND,
    EXTRACTOR_YIELD_MULTIPLIER,
} from '../rules/gameConfig'
import { MAX_CRATER_CELLS, MAX_CRATER_MARGIN } from '../rules/subplotDamage'
import { SUB_PER_PLOT, SUBPLOT_COUNT } from '../rules/subplotGrid'
import { subCenter, hexPoints, pixelToSub, columnSeamPoints, pointsAttr, HEX_COLS } from '../utils/hexGeometry'
import { LAYOUT, COLOR } from '../config/uiTokens'
import { R, BOARD_W, BOARD_H, LAND, HAIRLINE, ringPoints } from './v2/hex/geometry'
import {
    STRUCTURE_SHAPES,
    SHAPE_OFFSETS,
    emptyMakerHomefront,
    addStructureAt,
    removeStructureById,
    shapeFootprint,
    canPlaceFootprint,
    structureCount,
    shapeCounts,
    validateMaker,
    isV3Homefront,
} from '../rules/homefrontMaker'
import './HomefrontMakerV3.css'

// ─── Grid Maker ───────────────────────────────────────────────────────────────
// Author your Grid on the same hex lattice the war board draws it on: the
// self half of the board (utils/hexGeometry, the board's own R and land
// colours), one picture that scales to its slot. Pick a shape, then tap a
// hex — the tap is the shape's centre; tap a building to remove it. On a
// mouse the shape previews under the pointer (yellow where it can land, red
// where it can't) before the click.

const TIPS = [
    `A strike destroys a fixed blast around its aim hex — 7 hexes at margin 1, growing to ${MAX_CRATER_CELLS} at margin ${MAX_CRATER_MARGIN}+. Spread your structures so one blast can't take everything.`,
    'Blasts ignore corridor lines: a strike aimed near a boundary chews into the neighboring corridor\'s land too.',
    `A structure only dies when its LAST hex does — partial damage leaves it standing. Lose ${STRUCTURES_TO_WIN} structures and the war is lost; cratered land on its own scores nothing.`,
    'Pick a structure shape, then tap the field — the hex you tap becomes the structure\'s centre. Tap any structure to remove it again.',
    `Every lot earns ${YIELD_PER_LOT_PER_ROUND} yield per round, ${EXTRACTOR_YIELD_MULTIPLIER}× while its extractor stands on its network. Structures produce nothing themselves.`,
]

// A shape's hexes drawn small: its axial offsets laid out on a tiny flat-top
// lattice (the same maths as the board's), centred in the icon.
export function ShapeIcon({ shapeKey, r = 4.2, className = 'hfm__shape-icon' }) {
    const offsets = SHAPE_OFFSETS[shapeKey] ?? [[0, 0]]
    const h = Math.sqrt(3) * r
    const pts = offsets.map(([dq, dr]) => [1.5 * r * dq, h * (dr + dq / 2)])
    const xs = pts.map(p => p[0]), ys = pts.map(p => p[1])
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cy = (Math.min(...ys) + Math.max(...ys)) / 2
    const ext = 3.2 * r
    return (
        <svg className={className} viewBox={`${-ext} ${-ext} ${2 * ext} ${2 * ext}`} aria-hidden="true">
            {pts.map(([x, y], i) => <polygon key={i} className="hfm__shape-icon-hex" points={hexPoints(x - cx, y - cy, r * 0.94)} />)}
        </svg>
    )
}

// Roads and their Port are retired: a persisted grid's stale Port assignment
// is dropped on entry and nothing replaces it.
function stripPort(homefront) {
    const { port, ...rest } = homefront
    return rest
}

// The field is the board's self half, in board units: from the divider down.
const FIELD_Y0 = BOARD_H / 2
const FIELD_H = BOARD_H - FIELD_Y0

export default function HomefrontMakerV3({ onSave, initialHomefront }) {
    const [hf, setHf] = useState(() =>
        isV3Homefront(initialHomefront)
            ? stripPort(JSON.parse(JSON.stringify(initialHomefront)))
            : emptyMakerHomefront()
    )
    const [shape, setShape] = useState('single')      // STRUCTURE_SHAPES key
    const [tipIndex, setTipIndex] = useState(0)
    const [showIntro, setShowIntro] = useState(true)  // game-intro modal on entry
    const [hover, setHover] = useState(null)          // hex under a mouse pointer
    const svgRef = useRef(null)

    const structCount = structureCount(hf)
    const validation = validateMaker(hf)
    const isUnchanged = isV3Homefront(initialHomefront)
        ? JSON.stringify(hf) === JSON.stringify(stripPort(initialHomefront))
        : false

    // Static lattice: every self-half hex's centre and outline, the corridor seams.
    const geo = useMemo(() => {
        const cells = []
        for (let s = 0; s < SUBPLOT_COUNT; s++) {
            const { x, y } = subCenter('self', s, R)
            cells.push({ s, cx: x, cy: y, pts: hexPoints(x, y, R) })
        }
        const seams = []
        for (let sc = SUB_PER_PLOT - 1; sc < HEX_COLS - 1; sc += SUB_PER_PLOT) seams.push(pointsAttr(columnSeamPoints(sc, R)))
        return { cells, seams }
    }, [])

    // The hex under a pointer event, or null off the field.
    function hexFromEvent(e) {
        const svg = svgRef.current
        if (!svg) return null
        const rect = svg.getBoundingClientRect()
        if (!rect.width || !rect.height) return null
        const x = (e.clientX - rect.left) * (BOARD_W / rect.width)
        const y = FIELD_Y0 + (e.clientY - rect.top) * (FIELD_H / rect.height)
        const hit = pixelToSub(x, y, R)
        return hit && hit.side === 'self' ? hit.s : null
    }

    function structureTapAt(s) {
        if (s == null) return
        setHf(prev => {
            const existingId = prev.subplots[s]?.structureId
            if (existingId != null) return removeStructureById(prev, existingId)
            return addStructureAt(prev, shape, s)
        })
    }

    function handleAutoGenerate() {
        setHf(generateHomefrontV3())
    }

    function handleClearAll() {
        setHf(emptyMakerHomefront())
        setShape('single')
    }

    const structFull = structCount >= HOMEFRONT_MAX_STRUCTURES
    const usedByShape = shapeCounts(hf)
    const shapeExhausted = (usedByShape[shape] ?? 0) >= HOMEFRONT_MAX_PER_SHAPE

    // Preview under a mouse: the shape's footprint at the hovered hex, unless
    // the hex holds a building (a tap there removes it) or nothing more can
    // be placed.
    const preview = useMemo(() => {
        if (hover == null || structFull || shapeExhausted) return null
        if (hf.subplots[hover]?.structureId != null) return null
        const fp = shapeFootprint(shape, hover)
        return fp ? { cells: fp, ok: canPlaceFootprint(hf, fp) } : { cells: [hover], ok: false }
    }, [hover, shape, hf, structFull, shapeExhausted])

    return (
        <div className="hfm__screen">

            {/* Header — same height as war board header */}
            <div className="hfm__header">
                <span className="hfm__title">Set Up Your Hive</span>
            </div>

            {/* The war board's box, same size and place: the controls take the
                opponent's half, the field is your half. */}
            <div className="hfm__board" style={{ aspectRatio: `${BOARD_W} / ${BOARD_H}` }}>

            {/* Controls — occupies the "opp half" space */}
            <div className="hfm__controls">

                {/* Tips carousel */}
                <div className="hfm__tips-area">
                    <div className="hfm__tips">
                        <div className="hfm__tips-header">
                            <span className="hfm__tips-icon">💡</span>
                            <span className="hfm__tips-label">Tips</span>
                        </div>
                        <div className="hfm__tips-body">
                            <button
                                className="hfm__tips-arrow"
                                onClick={() => setTipIndex(i => (i - 1 + TIPS.length) % TIPS.length)}
                            >‹</button>
                            <p className="hfm__tips-text">{TIPS[tipIndex]}</p>
                            <button
                                className="hfm__tips-arrow"
                                onClick={() => setTipIndex(i => (i + 1) % TIPS.length)}
                            >›</button>
                        </div>
                    </div>
                </div>

                {/* Structure shape palette + count. */}
                <div className="hfm__struct-panel">
                    <div className="hfm__shape-row">
                        {STRUCTURE_SHAPES.map(sh => {
                            const used = usedByShape[sh.key] ?? 0
                            const exhausted = used >= HOMEFRONT_MAX_PER_SHAPE
                            return (
                                <button
                                    key={sh.key}
                                    data-testid={`shape-${sh.key}`}
                                    className={[
                                        'hfm__shape-btn',
                                        shape === sh.key ? 'hfm__shape-btn--selected' : '',
                                        exhausted ? 'hfm__shape-btn--exhausted' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => setShape(sh.key)}
                                >
                                    <ShapeIcon shapeKey={sh.key} />
                                    <span className="hfm__shape-label">{sh.label}</span>
                                    <span className="hfm__shape-count">{used}/{HOMEFRONT_MAX_PER_SHAPE}</span>
                                </button>
                            )
                        })}
                    </div>
                    <div className="hfm__struct-segs">
                        {Array.from({ length: validation.max }, (_, i) => (
                            <div
                                key={i}
                                className={[
                                    'hfm__struct-seg',
                                    i < structCount ? 'hfm__struct-seg--lit' : '',
                                    i < validation.min ? 'hfm__struct-seg--required' : '',
                                ].filter(Boolean).join(' ')}
                            />
                        ))}
                    </div>
                    <p className="hfm__struct-label">
                        {structFull
                            ? 'Structure limit reached — tap one to remove it'
                            : shapeExhausted
                                ? `No ${STRUCTURE_SHAPES.find(sh => sh.key === shape)?.label} structures left — pick another shape`
                                : structCount < validation.min
                                    ? `Place at least ${validation.min - structCount} more structure${validation.min - structCount !== 1 ? 's' : ''}`
                                    : `${validation.max - structCount} more allowed`}
                    </p>
                </div>

                {/* Action buttons */}
                <div className="hfm__actions">
                    <button className="hfm__btn-ghost" onClick={handleAutoGenerate}>Auto-generate</button>
                    <button className="hfm__btn-ghost" onClick={handleClearAll}>Clear All</button>
                </div>

            </div>

            {/* The field — your half of the war board, on its hex lattice */}
            <div className="hfm__grid-wrap">
                <svg
                    ref={svgRef}
                    className={`hfm__field${structFull ? ' hfm__field--full' : ''}`}
                    viewBox={`0 ${FIELD_Y0} ${BOARD_W} ${FIELD_H}`}
                    preserveAspectRatio="xMidYMin meet"
                    data-testid="maker-field"
                    onPointerDown={(e) => structureTapAt(hexFromEvent(e))}
                    onPointerMove={(e) => { if (e.pointerType === 'mouse') setHover(hexFromEvent(e)) }}
                    onPointerLeave={() => setHover(null)}
                >
                    {/* Land */}
                    <g className="hfm__land">
                        {geo.cells.map(({ s, pts }) => (
                            <polygon key={s} points={pts} fill={LAND.self.fill} stroke={HAIRLINE} strokeWidth="0.5" />
                        ))}
                    </g>
                    {/* Corridor seams — the board's own */}
                    <g fill="none" stroke={COLOR.BG} strokeWidth={LAYOUT.COL_GAP / 2} strokeLinejoin="round">
                        {geo.seams.map((p, i) => <polyline key={i} points={p} />)}
                    </g>
                    {/* Buildings: the board's own-structure wash and ring */}
                    <g>
                        {geo.cells.map(({ s, cx, cy, pts }) => (hf.subplots[s]?.structureId == null ? null : (
                            <g key={s} data-struct-s={s}>
                                <polygon points={pts} fill={`rgba(${COLOR.STRUCT_SELF_RGB}, 0.22)`} />
                                <polygon points={ringPoints(cx, cy, 1.5)} fill="none" stroke="rgba(0, 0, 0, 0.5)" strokeWidth="3" />
                                <polygon points={ringPoints(cx, cy, 1.5)} fill="none" stroke={COLOR.STRUCT_SELF} strokeWidth="1.5" />
                            </g>
                        )))}
                    </g>
                    {/* Preview under the mouse */}
                    {preview && (
                        <g style={{ pointerEvents: 'none' }} data-preview={preview.ok ? 'ok' : 'bad'}>
                            {preview.cells.map(s => {
                                const c = geo.cells[s]
                                return (
                                    <g key={s}>
                                        <polygon points={c.pts} fill={preview.ok ? 'rgba(240, 224, 64, 0.42)' : 'rgba(224, 80, 80, 0.5)'} />
                                        <polygon points={ringPoints(c.cx, c.cy, 1.5)} fill="none"
                                            stroke={preview.ok ? 'rgba(240, 224, 64, 0.95)' : 'rgba(224, 80, 80, 0.95)'} strokeWidth="1.5" />
                                    </g>
                                )
                            })}
                        </g>
                    )}
                </svg>
            </div>

            </div>

            {/* Lock Grid — below the field, matches the submit button */}
            <div className="hfm__lock-area">
                <button
                    className={`hfm__lock-btn${!validation.valid ? ' hfm__lock-btn--disabled' : ''}`}
                    onClick={() => { if (validation.valid) onSave(hf) }}
                >
                    {isUnchanged ? 'KEEP HIVE AS IS' : 'LOCK HIVE'}
                </button>
            </div>

            {/* Game-intro modal — anchored top so most of the field below
                stays visible behind the light backdrop. */}
            {showIntro && (
                <div className="hfm-intro__backdrop" onClick={() => setShowIntro(false)}>
                    <div className="hfm-intro__panel" onClick={e => e.stopPropagation()}>
                        <h2 className="hfm-intro__title">Your Hive</h2>

                        <div className="hfm-intro__section">
                            <h3 className="hfm-intro__heading">Build it</h3>
                            <p className="hfm-intro__text">
                                Place {HOMEFRONT_MAX_STRUCTURES} structures.
                                Every standing structure earns you yield each round of a
                                war — and every structure is a target.
                            </p>
                            <div className="hfm-intro__structure">
                                <ShapeIcon shapeKey="column" r={11} className="hfm-intro__structure-icon" />
                            </div>
                        </div>

                        <div className="hfm-intro__section">
                            <h3 className="hfm-intro__heading">Defend it</h3>
                            <p className="hfm-intro__text">
                                When you lose a battle, a strike hits your Hive
                                and destroys the land around its impact point.
                                The bigger the enemy's margin of victory, the
                                wider the blast.
                            </p>
                        </div>

                        <div className="hfm-intro__section">
                            <h3 className="hfm-intro__heading">Survive</h3>
                            <p className="hfm-intro__text">
                                A structure falls only when every one of its hexes
                                has been destroyed — partial damage leaves it
                                standing. Kill {STRUCTURES_TO_WIN} of your
                                enemy's {HOMEFRONT_MAX_STRUCTURES} structures to
                                win the war.
                            </p>
                        </div>

                        <button className="hfm-intro__btn" onClick={() => setShowIntro(false)}>
                            Build Your Hive
                        </button>
                    </div>
                </div>
            )}

        </div>
    )
}
